import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { GenreSelector, parseGenres, formatGenres } from "@/components/ui/genre-selector";
import { UserAvatar } from "@/components/UserAvatar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { oauthService } from "@/services/oauthService";
import {
  User,
  FileText,
  Gamepad2,
  Twitch,
  Youtube,
  Music,
  Instagram,
  Twitter,
  Save,
  Upload,
  Mail
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { TikTokConnection } from "@/components/TikTokConnection";
import { TwitchConnection } from "@/components/TwitchConnection";
import YouTubeConnection from "@/components/YouTubeConnection";
import { SteamConnection } from "@/components/SteamConnection";
import { DiscordConnection } from "@/components/DiscordConnection";
import { MessageSquare } from "lucide-react";

interface UserProfile {
  id: string;
  user_type: "developer" | "creator" | "player";
  display_name: string;
  avatar_url: string | null;
  bio: string | null;
  website_url: string | null;
  steam_url: string | null;
  steam_id: string | null;
  studio_name: string | null;
  studio_logo_url: string | null;
  platform: string | null;
  channel_url: string | null;
  subscriber_count: number | null;
  preferred_genres: string[] | null;
  first_name: string | null;
  last_name: string | null;
  birth_date: string | null;
  country: string | null;
  favorite_genres: string | null;
  twitch_url: string | null;
  twitch_username: string | null;
  twitch_connected: boolean | null;
  twitch_followers_count: number | null;
  twitch_subscribers_count: number | null;
  twitch_views_count: number | null;
  youtube_url: string | null;
  youtube_username: string | null;
  youtube_connected: boolean | null;
  youtube_subscribers_count: number | null;
  youtube_views_count: number | null;
  tiktok_url: string | null;
  tiktok_username: string | null;
  tiktok_connected: boolean | null;
  instagram_url: string | null;
  twitter_url: string | null;
  bluesky_url: string | null;
  other_platforms: string | null;
  studio_slug: string | null;
  is_newsletter?: boolean | null;
  discord_id: string | null;
  discord_username: string | null;
  discord_connected: boolean | null;
}


const countries = [
  "Afghanistan", "Albania", "Algeria", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan",
  "Bahrain", "Bangladesh", "Belarus", "Belgium", "Brazil", "Bulgaria", "Cambodia", "Canada", "Chile",
  "China", "Colombia", "Croatia", "Czech Republic", "Denmark", "Egypt", "Estonia", "Finland", "France",
  "Georgia", "Germany", "Ghana", "Greece", "Hungary", "Iceland", "India", "Indonesia", "Ireland",
  "Israel", "Italy", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kuwait", "Latvia", "Lebanon", "Lithuania",
  "Luxembourg", "Malaysia", "Mexico", "Morocco", "Netherlands", "New Zealand", "Nigeria", "Norway",
  "Pakistan", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Saudi Arabia",
  "Singapore", "Slovakia", "Slovenia", "South Africa", "South Korea", "Spain", "Sri Lanka", "Sweden",
  "Switzerland", "Thailand", "Turkey", "Ukraine", "United Arab Emirates", "United Kingdom", "United States",
  "Vietnam", "Other"
];

export default function Settings() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    display_name: "",
    bio: "",
    website_url: "",
    studio_name: "",
    platform: "",
    channel_url: "",
    subscriber_count: "",
    first_name: "",
    last_name: "",
    birth_date: "",
    country: "",
    favorite_genres: [] as string[],
    twitch_url: "",
    twitch_username: "",
    twitch_connected: false,
    twitch_followers_count: 0,
    twitch_subscribers_count: 0,
    twitch_views_count: 0,
    youtube_url: "",
    youtube_username: "",
    youtube_connected: false,
    youtube_subscribers_count: 0,
    youtube_views_count: 0,
    tiktok_url: "",
    tiktok_username: "",
    tiktok_connected: false,
    instagram_url: "",
    twitter_url: "",
    bluesky_url: "",
    steam_url: "",
    steam_id: "",
    studio_slug: "",
    is_newsletter: false,
    discord_id: "",
    discord_username: "",
    discord_connected: false,
  });

  useEffect(() => {
    if (user?.id) {
      fetchProfile();
    }
  }, [user?.id]);

  const fetchProfile = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*, shop_items(metadata)")
        .eq("id", user.id)
        .single();

      if (error) {
        console.error("Error fetching profile:", error);
        toast.error("Failed to load profile");
        return;
      }

      setProfile(data as unknown as UserProfile);

      // Update form data
      const newFormData = {
        display_name: data.display_name || "",
        bio: data.bio || "",
        website_url: data.website_url || "",
        studio_name: data.studio_name || "",
        platform: data.platform || "",
        channel_url: data.channel_url || "",
        subscriber_count: data.subscriber_count?.toString() || "",
        first_name: (data as any).first_name || "",
        last_name: (data as any).last_name || "",
        birth_date: (data as any).birth_date || "",
        country: (data as any).country || "",
        favorite_genres: data.favorite_genres ? parseGenres(data.favorite_genres) : [],
        twitch_url: data.twitch_url || "",
        twitch_username: (data as any).twitch_username || "",
        twitch_connected: (data as any).twitch_connected || false,
        twitch_followers_count: (data as any).twitch_followers_count || 0,
        twitch_subscribers_count: (data as any).twitch_subscribers_count || 0,
        twitch_views_count: (data as any).twitch_views_count || 0,
        youtube_url: data.youtube_url || "",
        youtube_username: (data as any).youtube_username || "",
        youtube_connected: (data as any).youtube_connected || false,
        youtube_subscribers_count: (data as any).youtube_subscribers_count || 0,
        youtube_views_count: (data as any).youtube_views_count || 0,
        tiktok_url: data.tiktok_url || "",
        tiktok_username: (data as any).tiktok_username || "",
        tiktok_connected: (data as any).tiktok_connected || false,
        instagram_url: data.instagram_url || "",
        twitter_url: data.twitter_url || "",
        bluesky_url: data.bluesky_url || "",
        steam_url: data.steam_url || "",
        steam_id: data.steam_id || "",
        studio_slug: data.studio_slug || "",
        is_newsletter: (data as any).is_newsletter || false,
        discord_id: (data as any).discord_id || "",
        discord_username: (data as any).discord_username || "",
        discord_connected: (data as any).discord_connected || false,
      };

      setFormData(newFormData);
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    if (field === 'studio_slug') {
      // During typing: allow lowercase, numbers and hyphens; do not trim/collapse so '-' is allowed while typing
      const normalized = String(value)
        .toLowerCase()
        .replace(/[^a-z0-9-]+/g, '');
      setFormData(prev => ({ ...prev, [field]: normalized }));
      return;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenresChange = (genres: string[]) => {
    setFormData(prev => ({ ...prev, favorite_genres: genres }));
  };

  const handleTwitchConnect = async (username?: string) => {
    // Éviter les appels multiples
    if (formData.twitch_connected) {
      return;
    }

    const twitchUsername = username || "twitch_user";

    // Récupérer les données depuis localStorage
    const twitchProfile = localStorage.getItem('twitch_user_profile');
    let followersCount = 0;
    let subscribersCount = 0;
    let viewsCount = 0;

    if (twitchProfile) {
      try {
        const profile = JSON.parse(twitchProfile);
        followersCount = profile.followersCount || 0;
        subscribersCount = profile.subscribersCount || 0;
        viewsCount = profile.viewsCount || 0;
      } catch (error) {
        console.error('Error parsing Twitch profile:', error);
      }
    }

    // Mettre à jour le formData
    setFormData(prev => ({
      ...prev,
      twitch_connected: true,
      twitch_username: twitchUsername,
      twitch_url: `https://twitch.tv/${twitchUsername}`,
      twitch_followers_count: followersCount,
      twitch_subscribers_count: subscribersCount,
      twitch_views_count: viewsCount
    }));

    // Sauvegarder immédiatement en BDD
    if (profile) {
      try {
        const { error } = await supabase
          .from("profiles")
          .update({
            twitch_connected: true,
            twitch_username: twitchUsername,
            twitch_url: `https://twitch.tv/${twitchUsername}`,
            twitch_followers_count: followersCount,
            twitch_subscribers_count: subscribersCount,
            twitch_views_count: viewsCount
          } as any)
          .eq("id", profile.id);

        if (error) {
          console.error("Error updating Twitch data:", error);
          toast.error("Failed to save Twitch data");
        } else {
          toast.success("Twitch data saved successfully!");
        }
      } catch (error) {
        console.error("Error saving Twitch data:", error);
        toast.error("Failed to save Twitch data");
      }
    }
  };

  const handleTwitchDisconnect = async () => {
    // Mettre à jour le formData
    setFormData(prev => ({
      ...prev,
      twitch_connected: false,
      twitch_username: "",
      twitch_url: "",
      twitch_followers_count: 0,
      twitch_subscribers_count: 0,
      twitch_views_count: 0
    }));

    // Sauvegarder en BDD
    if (profile) {
      try {
        const { error } = await supabase
          .from("profiles")
          .update({
            twitch_connected: false,
            twitch_username: null,
            twitch_url: null,
            twitch_followers_count: 0,
            twitch_subscribers_count: 0,
            twitch_views_count: 0
          } as any)
          .eq("id", profile.id);

        if (error) {
          console.error("Error updating Twitch disconnect:", error);
          toast.error("Failed to disconnect Twitch");
        } else {
          toast.success("Twitch disconnected successfully!");
        }
      } catch (error) {
        console.error("Error disconnecting Twitch:", error);
        toast.error("Failed to disconnect Twitch");
      }
    }
  };

  const handleTwitchRefresh = async () => {
    if (!user || !formData.twitch_connected) return;

    try {
      // Récupérer le token d'accès depuis localStorage
      const accessToken = localStorage.getItem('twitch_access_token');
      const clientId = import.meta.env.VITE_TWITCH_CLIENT_ID;

      if (!accessToken || !clientId) {
        toast.error("Twitch not properly connected");
        return;
      }

      // Récupérer les données utilisateur
      const userResponse = await fetch('https://api.twitch.tv/helix/users', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Client-Id': clientId,
          'Accept': 'application/json'
        }
      });

      if (!userResponse.ok) {
        throw new Error('Failed to fetch user data');
      }

      const userData = await userResponse.json();
      const user = userData.data[0];
      const userId = user.id;

      // Récupérer les données de la chaîne
      const channelResponse = await fetch(`https://api.twitch.tv/helix/channels?broadcaster_id=${userId}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Client-Id': clientId,
          'Accept': 'application/json'
        }
      });

      let channelData = null;
      if (channelResponse.ok) {
        const channelResult = await channelResponse.json();
        channelData = channelResult.data[0];
      }

      // Essayer de récupérer les followers
      let followersCount = 0;
      try {
        const followersResponse = await fetch(`https://api.twitch.tv/helix/channels/followers?broadcaster_id=${userId}&first=1`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Client-Id': clientId,
            'Accept': 'application/json'
          }
        });

        if (followersResponse.ok) {
          const followersResult = await followersResponse.json();
          followersCount = followersResult.total || 0;
        } else if (followersResponse.status === 401) {
          // Followers API requires additional scopes, skipping...
        }
      } catch (followersError) {
        // Followers API not accessible
      }

      // Essayer de récupérer les subscribers
      let subscribersCount = 0;
      try {
        const subscribersResponse = await fetch(`https://api.twitch.tv/helix/subscriptions?broadcaster_id=${userId}&first=1`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Client-Id': clientId,
            'Accept': 'application/json'
          }
        });

        if (subscribersResponse.ok) {
          const subscribersResult = await subscribersResponse.json();
          subscribersCount = subscribersResult.total || 0;
        } else if (subscribersResponse.status === 401) {
          // Subscribers API requires additional scopes, skipping...
        }
      } catch (subscribersError) {
        // Subscribers API not accessible
      }

      const viewsCount = channelData?.view_count || 0;

      // Mettre à jour le formData
      setFormData(prev => ({
        ...prev,
        twitch_followers_count: followersCount,
        twitch_subscribers_count: subscribersCount,
        twitch_views_count: viewsCount
      }));

      // Sauvegarder en BDD
      const { error } = await supabase
        .from("profiles")
        .update({
          twitch_followers_count: followersCount,
          twitch_subscribers_count: subscribersCount,
          twitch_views_count: viewsCount
        } as any)
        .eq("id", profile.id);

      if (error) {
        console.error("Error updating Twitch data:", error);
        toast.error("Failed to refresh Twitch data");
      } else {
        toast.success("Twitch data refreshed successfully!");
      }

    } catch (error) {
      console.error("Error refreshing Twitch data:", error);
      toast.error("Failed to refresh Twitch data");
    }
  };

  const handleTikTokConnect = (username?: string) => {
    const tiktokUsername = username || "tiktok_user";
    setFormData(prev => ({
      ...prev,
      tiktok_connected: true,
      tiktok_username: tiktokUsername,
      tiktok_url: `https://tiktok.com/@${tiktokUsername}`
    }));
  };

  const handleTikTokDisconnect = () => {
    setFormData(prev => ({
      ...prev,
      tiktok_connected: false,
      tiktok_username: "",
      tiktok_url: ""
    }));
  };

  const handleYouTubeConnect = async (username?: string) => {
    // Éviter les appels multiples
    if (formData.youtube_connected) {
      return;
    }

    // Récupérer les données depuis localStorage (YouTube utilise Google OAuth)
    const googleProfile = localStorage.getItem('google_user_profile');

    let parsedGoogleProfile = null;
    if (googleProfile) {
      try {
        parsedGoogleProfile = JSON.parse(googleProfile);
      } catch (error) {
        console.error('Error parsing Google profile:', error);
      }
    }

    // Utiliser le displayName du profil Google si disponible, sinon le username passé en paramètre
    let youtubeUsername = "youtube_user";
    if (parsedGoogleProfile?.displayName) {
      youtubeUsername = parsedGoogleProfile.displayName;
    } else if (username) {
      youtubeUsername = username;
    }

    let subscribersCount = 0;
    let viewsCount = 0;

    if (googleProfile) {
      try {
        const profile = JSON.parse(googleProfile);
        subscribersCount = profile.subscribersCount || 0;
        viewsCount = profile.viewsCount || 0;
      } catch (error) {
        console.error('Error parsing Google profile:', error);
      }
    }

    // Si on n'a pas les stats dans le profil, les récupérer via l'API
    if (subscribersCount === 0 && viewsCount === 0) {
      try {
        const accessToken = localStorage.getItem('google_access_token');
        if (accessToken) {

          // Récupérer l'ID de la chaîne YouTube
          const channelResponse = await fetch('https://www.googleapis.com/youtube/v3/channels?part=id&mine=true', {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Accept': 'application/json'
            }
          });

          if (channelResponse.ok) {
            const channelData = await channelResponse.json();
            const channelId = channelData.items?.[0]?.id;

            if (channelId) {

              // Récupérer les statistiques
              const statsResponse = await fetch(`https://www.googleapis.com/youtube/v3/channels?part=statistics&id=${channelId}`, {
                headers: {
                  'Authorization': `Bearer ${accessToken}`,
                  'Accept': 'application/json'
                }
              });

              if (statsResponse.ok) {
                const statsData = await statsResponse.json();
                const stats = statsData.items?.[0]?.statistics;

                if (stats) {
                  subscribersCount = parseInt(stats.subscriberCount || '0');
                  viewsCount = parseInt(stats.viewCount || '0');
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching YouTube stats on connection:', error);
      }
    }

    // Mettre à jour le formData

    setFormData(prev => ({
      ...prev,
      youtube_connected: true,
      youtube_username: youtubeUsername,
      youtube_url: `https://www.youtube.com/@${youtubeUsername}`,
      youtube_subscribers_count: subscribersCount,
      youtube_views_count: viewsCount
    }));

    // Sauvegarder immédiatement en BDD
    if (profile) {
      try {
        const { error } = await supabase
          .from("profiles")
          .update({
            youtube_connected: true,
            youtube_username: youtubeUsername,
            youtube_url: `https://www.youtube.com/@${youtubeUsername}`,
            youtube_subscribers_count: subscribersCount,
            youtube_views_count: viewsCount
          } as any)
          .eq("id", profile.id);

        if (error) {
          console.error("Error updating YouTube data:", error);
          toast.error("Failed to save YouTube data");
        } else {
          toast.success("YouTube connected successfully!");
        }
      } catch (error) {
        console.error("Error saving YouTube data:", error);
        toast.error("Failed to save YouTube data");
      }
    }
  };

  const handleYouTubeDisconnect = async () => {
    // Mettre à jour le formData
    setFormData(prev => ({
      ...prev,
      youtube_connected: false,
      youtube_username: "",
      youtube_url: "",
      youtube_subscribers_count: 0,
      youtube_views_count: 0
    }));

    // Sauvegarder en BDD
    if (profile) {
      try {
        const { error } = await supabase
          .from("profiles")
          .update({
            youtube_connected: false,
            youtube_username: null,
            youtube_url: null,
            youtube_subscribers_count: 0,
            youtube_views_count: 0
          } as any)
          .eq("id", profile.id);

        if (error) {
          console.error("Error updating YouTube disconnect:", error);
          toast.error("Failed to disconnect YouTube");
        } else {
          toast.success("YouTube disconnected successfully!");
        }
      } catch (error) {
        console.error("Error disconnecting YouTube:", error);
        toast.error("Failed to disconnect YouTube");
      }
    }
  };

  const handleYouTubeRefresh = async () => {
    if (!user || !formData.youtube_connected) return;

    try {
      // Récupérer le token d'accès depuis localStorage (YouTube utilise Google OAuth)
      const accessToken = localStorage.getItem('google_access_token');

      if (!accessToken) {
        toast.error("YouTube not properly connected");
        return;
      }

      // Récupérer les données utilisateur
      const userResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Accept': 'application/json'
        }
      });

      if (!userResponse.ok) {
        throw new Error('Failed to fetch user data');
      }

      const userData = await userResponse.json();

      // Récupérer l'ID de la chaîne YouTube
      const channelResponse = await fetch('https://www.googleapis.com/youtube/v3/channels?part=id&mine=true', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Accept': 'application/json'
        }
      });

      let channelId = null;
      let subscribersCount = 0;
      let viewsCount = 0;

      if (channelResponse.ok) {
        const channelResult = await channelResponse.json();
        if (channelResult.items && channelResult.items.length > 0) {
          channelId = channelResult.items[0].id;
        }
      } else if (channelResponse.status === 401) {
        const errorText = await channelResponse.text();
        console.error('❌ [Refresh] Error details:', errorText);
      } else {
        const errorText = await channelResponse.text();
        console.error('❌ [Refresh] Channel API error:', errorText);
      }

      // Si on a un ID de chaîne, récupérer les statistiques détaillées
      if (channelId) {
        try {
          const statsResponse = await fetch(`https://www.googleapis.com/youtube/v3/channels?part=statistics&id=${channelId}`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Accept': 'application/json'
            }
          });


          if (statsResponse.ok) {
            const statsResult = await statsResponse.json();
            if (statsResult.items && statsResult.items.length > 0) {
              const stats = statsResult.items[0].statistics;
              subscribersCount = parseInt(stats.subscriberCount) || 0;
              viewsCount = parseInt(stats.viewCount) || 0;
            }
          } else {
            const errorText = await statsResponse.text();
            console.error('❌ [Refresh] Stats API error:', errorText);
          }
        } catch (statsError) {
          // YouTube stats API not accessible
        }
      }

      // Mettre à jour le formData
      setFormData(prev => ({
        ...prev,
        youtube_subscribers_count: subscribersCount,
        youtube_views_count: viewsCount
      }));

      // Sauvegarder en BDD
      const { error } = await supabase
        .from("profiles")
        .update({
          youtube_subscribers_count: subscribersCount,
          youtube_views_count: viewsCount
        } as any)
        .eq("id", profile.id);

      if (error) {
        console.error("❌ [Refresh] Error updating YouTube data:", error);
        toast.error("Failed to refresh YouTube data");
      } else {
        toast.success("YouTube data refreshed successfully!");
      }

    } catch (error) {
      console.error("Error refreshing YouTube data:", error);
      toast.error("Failed to refresh YouTube data");
    }
  };


  const handleDiscordConnect = async (username?: string) => {
    if (formData.discord_connected) return;

    setFormData(prev => ({
      ...prev,
      discord_connected: true,
      discord_username: username || "discord_user",
    }));

    if (profile) {
      try {
        const { error } = await supabase
          .from("profiles")
          .update({
            discord_connected: true,
            discord_username: username || "discord_user",
          } as any)
          .eq("id", profile.id);

        if (error) {
          console.error("Error updating Discord data:", error);
          toast.error("Failed to save Discord data");
        } else {
          // Sync metadata with Discord
          const accessToken = localStorage.getItem('discord_access_token');
          if (accessToken && profile) {
            await oauthService.updateDiscordMetadata(
              accessToken,
              profile.user_type,
              username || "IndieKeys User"
            );
          }
          toast.success("Discord connection saved and roles synchronized!");
        }
      } catch (error) {
        console.error("Error saving Discord data:", error);
      }
    }
  };

  const handleDiscordDisconnect = async () => {
    setFormData(prev => ({
      ...prev,
      discord_connected: false,
      discord_username: "",
      discord_id: "",
    }));

    if (profile) {
      try {
        const { error } = await supabase
          .from("profiles")
          .update({
            discord_connected: false,
            discord_username: null,
            discord_id: null,
          } as any)
          .eq("id", profile.id);

        if (error) {
          console.error("Error updating Discord disconnect:", error);
          toast.error("Failed to disconnect Discord");
        } else {
          toast.success("Discord disconnected!");
        }
      } catch (error) {
        console.error("Error disconnecting Discord:", error);
      }
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      setAvatarFile(file);

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setAvatarPreview(previewUrl);
    }
  };

  const uploadAvatar = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `avatar-${user?.id}-${Date.now()}.${fileExt}`;
    const filePath = `${user?.id}/${fileName}`;

    // Utiliser le bucket avatars
    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filePath, file);

    if (uploadError) {
      console.error("Upload error details:", uploadError);
      throw new Error(`Upload failed: ${uploadError.message}`);
    }

    const { data } = supabase.storage
      .from('avatars')
      .getPublicUrl(filePath);

    return data.publicUrl;
  };


  const handleSave = async () => {
    if (!user || !profile) return;

    setSaving(true);
    try {
      // Finalize slug: trim leading/trailing hyphens and collapse duplicates before checks/saving
      const finalizedSlug = formData.studio_slug
        ? String(formData.studio_slug)
          .toLowerCase()
          .replace(/--+/g, '-')
          .replace(/^-+|-+$/g, '')
        : '';

      if (formData.studio_slug !== finalizedSlug) {
        setFormData(prev => ({ ...prev, studio_slug: finalizedSlug }));
      }

      // Uniqueness check for studio_slug (only for developers and non-empty)
      if (profile.user_type === 'developer' && finalizedSlug) {
        const { data: slugRows, error: slugErr } = await supabase
          .from('profiles')
          .select('id')
          .eq('studio_slug', finalizedSlug)
          .neq('id', user.id)
          .limit(1);
        if (slugErr) {
          console.error('Error checking slug uniqueness:', slugErr);
          toast.error('Failed to validate studio URL. Please try again.');
          setSaving(false);
          return;
        }
        if (slugRows && slugRows.length > 0) {
          toast.error('This studio URL is already taken. Please choose another.');
          setSaving(false);
          return;
        }
      }
      let avatarUrl = profile.avatar_url;

      // Upload new avatar if selected
      if (avatarFile) {
        try {
          avatarUrl = await uploadAvatar(avatarFile);
        } catch (uploadError: any) {
          console.error("Avatar upload error:", uploadError);
          toast.error(`Failed to upload avatar: ${uploadError.message || 'Unknown error'}`);
          setSaving(false);
          return;
        }
      }

      // Prepare update data - include all fields, even empty ones
      // Note: display_name is excluded to prevent modification after registration
      const updateData = {
        bio: formData.bio || null,
        website_url: formData.website_url || null,
        steam_url: formData.steam_url || null,
        steam_id: formData.steam_id || null,
        studio_name: formData.studio_name || null,
        platform: formData.platform || null,
        channel_url: formData.channel_url || null,
        subscriber_count: formData.subscriber_count ? parseInt(formData.subscriber_count) : null,
        avatar_url: avatarUrl || profile.avatar_url,
        first_name: formData.first_name || null,
        last_name: formData.last_name || null,
        birth_date: formData.birth_date || null,
        country: formData.country || null,
        favorite_genres: formData.favorite_genres.length > 0 ? formatGenres(formData.favorite_genres) : null,
        twitch_url: formData.twitch_url || null,
        twitch_username: formData.twitch_username || null,
        twitch_connected: formData.twitch_connected || false,
        twitch_followers_count: formData.twitch_followers_count || 0,
        twitch_subscribers_count: formData.twitch_subscribers_count || 0,
        twitch_views_count: formData.twitch_views_count || 0,
        youtube_url: formData.youtube_url || null,
        youtube_username: formData.youtube_username || null,
        youtube_connected: formData.youtube_connected || false,
        youtube_subscribers_count: formData.youtube_subscribers_count || 0,
        youtube_views_count: formData.youtube_views_count || 0,
        tiktok_url: formData.tiktok_url || null,
        tiktok_username: formData.tiktok_username || null,
        tiktok_connected: formData.tiktok_connected || false,
        instagram_url: formData.instagram_url || null,
        twitter_url: formData.twitter_url || null,
        bluesky_url: formData.bluesky_url || null,
        studio_slug: finalizedSlug || null,
        is_newsletter: formData.is_newsletter,
        discord_id: formData.discord_id || null,
        discord_username: formData.discord_username || null,
        discord_connected: formData.discord_connected || false,
      };


      // Essayer d'abord une mise à jour complète
      const { error } = await supabase
        .from("profiles")
        .update(updateData)
        .eq("id", user.id);

      if (error) {
        console.error("Error updating profile:", error);
        console.error("Error details:", error.details);
        console.error("Error hint:", error.hint);
        console.error("Error code:", error.code);

        // Si l'erreur est liée à la taille, essayer par petits lots
        if (error.message.includes('too large') || error.message.includes('timeout') || error.code === 'PGRST301') {

          // Diviser en lots plus petits
          const fieldsToUpdate = Object.keys(updateData);
          const batchSize = 5;

          for (let i = 0; i < fieldsToUpdate.length; i += batchSize) {
            const batch = fieldsToUpdate.slice(i, i + batchSize);
            const batchData: any = {};

            batch.forEach(field => {
              batchData[field] = updateData[field];
            });

            const { error: batchError } = await supabase
              .from("profiles")
              .update(batchData)
              .eq("id", user.id);

            if (batchError) {
              console.error(`Error updating batch ${Math.floor(i / batchSize) + 1}:`, batchError);
              toast.error(`Failed to update profile (batch ${Math.floor(i / batchSize) + 1}): ${batchError.message}`);
              return;
            }

            // Pause entre les lots
            if (i + batchSize < fieldsToUpdate.length) {
              await new Promise(resolve => setTimeout(resolve, 200));
            }
          }
        } else {
          toast.error(`Failed to update profile: ${error.message}`);
          return;
        }
      }

      // Sync with Brevo
      if (user?.email && profile?.user_type) {
        supabase.functions.invoke('update-brevo-contact', {
          body: {
            email: user.email,
            is_newsletter: formData.is_newsletter || false,
            user_type: profile.user_type,
            display_name: formData.display_name
          }
        }).catch(err => console.error('Failed to sync with Brevo:', err));
      }

      toast.success("Profile updated successfully");
      setAvatarFile(null);
      setAvatarPreview(null);
      fetchProfile(); // Refresh profile data
    } catch (error) {
      console.error("Error:", error);
      toast.error(`An error occurred: ${error instanceof Error ? error.message : 'Unknown error'} `);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!profile) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card className="p-8 text-center">
            <h1 className="text-2xl font-bold mb-4">Profile not found</h1>
            <p className="text-muted-foreground">Unable to load your profile information.</p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Settings</h1>
          <p className="text-muted-foreground">Manage your profile and preferences</p>
        </div>

        <div className="space-y-6">
          {/* Profile Picture & Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Profile Information
              </CardTitle>
              <CardDescription>Your basic profile details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="flex flex-col items-center">
                  <UserAvatar
                    src={avatarPreview || profile.avatar_url || undefined}
                    fallback={profile.display_name?.charAt(0) || "U"}
                    size="xl"
                    borderStyle={(profile as any).shop_items?.metadata?.style}
                    className="mb-4"
                  />
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <Button variant="outline" size="sm" className="pointer-events-none">
                      <Upload className="w-4 h-4 mr-2" />
                      Change Avatar
                    </Button>
                  </div>
                </div>

                <div className="flex-1 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="display_name">Display Name *</Label>
                      <Input
                        id="display_name"
                        value={formData.display_name}
                        disabled
                        placeholder="Your display name"
                        className="bg-muted cursor-not-allowed"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Display name cannot be changed after registration
                      </p>
                    </div>

                    {/* Personal info fields - show for creators and developers */}
                    {(profile.user_type === "creator" || profile.user_type === "developer") && (
                      <>
                        {/* First Name and Last Name side by side */}
                        <div className="grid grid-cols-2 gap-4 sm:col-span-2">
                          <div>
                            <Label htmlFor="first_name">First Name</Label>
                            <Input
                              id="first_name"
                              value={formData.first_name}
                              onChange={(e) => handleInputChange("first_name", e.target.value)}
                              placeholder="Your first name"
                            />
                          </div>
                          <div>
                            <Label htmlFor="last_name">Last Name</Label>
                            <Input
                              id="last_name"
                              value={formData.last_name}
                              onChange={(e) => handleInputChange("last_name", e.target.value)}
                              placeholder="Your last name"
                            />
                          </div>
                        </div>

                        {profile.user_type === "creator" && (
                          <div>
                            <Label htmlFor="birth_date">Birth Date</Label>
                            <Input
                              id="birth_date"
                              type="date"
                              value={formData.birth_date}
                              onChange={(e) => handleInputChange("birth_date", e.target.value)}
                            />
                          </div>
                        )}
                      </>
                    )}

                    {/* Country field - available for everyone */}
                    <div className="sm:col-span-2">
                      <Label htmlFor="country">Country</Label>
                      <Select value={formData.country} onValueChange={(value) => handleInputChange("country", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select your country" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((country) => (
                            <SelectItem key={country} value={country}>
                              {country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="bio">Bio</Label>
                    <Textarea
                      id="bio"
                      value={formData.bio}
                      onChange={(e) => handleInputChange("bio", e.target.value)}
                      placeholder="Tell us about yourself..."
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Gaming Preferences - Only show for creators */}
          {profile.user_type === "creator" || profile.user_type === "player" && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gamepad2 className="w-5 h-5" />
                  Gaming Preferences
                </CardTitle>
                <CardDescription>Your favorite game genres</CardDescription>
              </CardHeader>
              <CardContent>
                <div>
                  <Label>Favorite Genres</Label>
                  <div className="mt-2">
                    <GenreSelector
                      value={formData.favorite_genres}
                      onChange={handleGenresChange}
                      placeholder="Select your favorite genres..."
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Creator/Developer Specific Info */}
          {/*profile.user_type === "creator" && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Creator Information
                </CardTitle>
                <CardDescription>Your content creation details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="platform">Main Platform</Label>
                    <Select value={formData.platform} onValueChange={(value) => handleInputChange("platform", value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select your main platform" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="youtube">YouTube</SelectItem>
                        <SelectItem value="twitch">Twitch</SelectItem>
                        <SelectItem value="tiktok">TikTok</SelectItem>
                        <SelectItem value="instagram">Instagram</SelectItem>
                        <SelectItem value="twitter">Twitter</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="channel_url">Channel URL</Label>
                    <Input
                      id="channel_url"
                      value={formData.channel_url}
                      onChange={(e) => handleInputChange("channel_url", e.target.value)}
                      placeholder="https://youtube.com/@yourchannel"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="subscriber_count">Subscriber Count</Label>
                    <Input
                      id="subscriber_count"
                      type="number"
                      value={formData.subscriber_count}
                      onChange={(e) => handleInputChange("subscriber_count", e.target.value)}
                      placeholder="1000000"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )*/}

          {profile.user_type === "developer" && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Developer Information
                </CardTitle>
                <CardDescription>Your development studio details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="studio_name">Studio Name</Label>
                    <Input
                      id="studio_name"
                      value={formData.studio_name}
                      onChange={(e) => handleInputChange("studio_name", e.target.value)}
                      placeholder="Your studio name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="website_url">Website URL</Label>
                    <Input
                      id="website_url"
                      value={formData.website_url}
                      onChange={(e) => handleInputChange("website_url", e.target.value)}
                      placeholder="https://yourstudio.com"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="studio_slug">URL Slug</Label>
                    <Input
                      id="studio_slug"
                      value={formData.studio_slug}
                      onChange={(e) => handleInputChange("studio_slug", e.target.value)}
                      placeholder="your-studio-slug"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Lowercase, letters and numbers only, hyphens allowed. Example: patate-douce-studio
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Social Media Links */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Twitter className="w-5 h-5" />
                Social Media
              </CardTitle>
              <CardDescription>Your social media profiles</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
                {profile.user_type === "creator" ? (
                  <div>
                    <TwitchConnection
                      value={formData.twitch_url}
                      onChange={(value) => handleInputChange("twitch_url", value)}
                      connected={formData.twitch_connected}
                      onConnect={handleTwitchConnect}
                      onDisconnect={handleTwitchDisconnect}
                      onRefresh={handleTwitchRefresh}
                      loading={saving}
                    />

                  </div>
                ) : (
                  <div>
                    <Label htmlFor="twitch_url" className="flex items-center gap-2">
                      <Twitch className="w-4 h-4" />
                      Twitch
                    </Label>
                    <Input
                      id="twitch_url"
                      value={formData.twitch_url}
                      onChange={(e) => handleInputChange("twitch_url", e.target.value)}
                      placeholder="https://twitch.tv/yourchannel"
                    />
                  </div>
                )}
                {profile.user_type === "creator" ? (
                  <div>
                    <YouTubeConnection
                      value={formData.youtube_url}
                      onChange={(value) => handleInputChange("youtube_url", value)}
                      onConnect={handleYouTubeConnect}
                      onDisconnect={handleYouTubeDisconnect}
                      onRefresh={handleYouTubeRefresh}
                      loading={saving}
                      subscribersCount={formData.youtube_subscribers_count}
                      viewsCount={formData.youtube_views_count}
                    />
                  </div>
                ) : (
                  <div>
                    <Label htmlFor="youtube_url" className="flex items-center gap-2">
                      <Youtube className="w-4 h-4" />
                      YouTube
                    </Label>
                    <Input
                      id="youtube_url"
                      value={formData.youtube_url}
                      onChange={(e) => handleInputChange("youtube_url", e.target.value)}
                      placeholder="https://youtube.com/@yourchannel"
                    />
                  </div>
                )}
                {profile.user_type === "creator" ? (
                  <TikTokConnection
                    value={formData.tiktok_url}
                    onChange={(value) => handleInputChange("tiktok_url", value)}
                    connected={formData.tiktok_connected}
                    onConnect={handleTikTokConnect}
                    onDisconnect={handleTikTokDisconnect}
                    loading={saving}
                  />
                ) : (
                  <div>
                    <Label htmlFor="tiktok_url" className="flex items-center gap-2">
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                      </svg>
                      TikTok
                    </Label>
                    <Input
                      id="tiktok_url"
                      value={formData.tiktok_url}
                      onChange={(e) => handleInputChange("tiktok_url", e.target.value)}
                      placeholder="https://tiktok.com/@yourchannel"
                    />
                  </div>
                )}
                <div>
                  <Label htmlFor="instagram_url" className="flex items-center gap-2 mb-1">
                    <Instagram className="w-4 h-4" />
                    Instagram
                  </Label>
                  <Input
                    id="instagram_url"
                    value={formData.instagram_url}
                    onChange={(e) => handleInputChange("instagram_url", e.target.value)}
                    placeholder="https://instagram.com/yourchannel"
                  />
                </div>
                <div>
                  <Label htmlFor="twitter_url" className="flex items-center gap-2 mb-1">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                    X (Twitter)
                  </Label>
                  <Input
                    id="twitter_url"
                    value={formData.twitter_url}
                    onChange={(e) => handleInputChange("twitter_url", e.target.value)}
                    placeholder="https://twitter.com/yourchannel"
                  />
                </div>
                <div>
                  <Label htmlFor="bluesky_url" className="flex items-center gap-2 mb-1">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 10.8c-1.087-2.183-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.22-.254.455-.345.705-.18.5-.275 1.03-.275 1.58 0 1.418.488 2.73 1.375 3.78.89 1.05 2.205 1.65 3.625 1.65s2.735-.6 3.625-1.65c.887-1.05 1.375-2.362 1.375-3.78 0-.55-.095-1.08-.275-1.58-.091-.25-.207-.485-.345-.705.14.017.279.036.415.056 2.67.296 5.568-.628 6.383-3.364.246-.829.624-5.79.624-6.478 0-.69-.139-2.861-.902-3.204-.659-.299-1.664-.621-4.3 1.24C16.046 4.747 13.087 8.617 12 10.8z" />
                    </svg>
                    Bluesky
                  </Label>
                  <Input
                    id="bluesky_url"
                    value={formData.bluesky_url}
                    onChange={(e) => handleInputChange("bluesky_url", e.target.value)}
                    placeholder="https://bsky.app/profile/yourchannel"
                  />
                </div>
                {profile.user_type === "developer" ? (
                  <div>
                    <Label htmlFor="steam_url" className="flex items-center gap-2 mb-1">
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.5 1.009 2.455-.397.957-1.497 1.41-2.454 1.012H7.54zm11.415-9.303c0-1.662-1.353-3.015-3.015-3.015-1.665 0-3.015 1.353-3.015 3.015 0 1.665 1.35 3.015 3.015 3.015 1.663 0 3.015-1.35 3.015-3.015zm-5.273-.005c0-1.252 1.013-2.266 2.265-2.266 1.249 0 2.266 1.014 2.266 2.266 0 1.251-1.017 2.265-2.266 2.265-1.253 0-2.265-1.014-2.265-2.265z" />
                      </svg>
                      Steam
                    </Label>
                    <Input
                      id="steam_url"
                      value={formData.steam_url}
                      onChange={(e) => handleInputChange("steam_url", e.target.value)}
                      placeholder="https://steamcommunity.com/id/yourusername"
                    />
                  </div>
                ) : (
                  <div>
                    <SteamConnection />
                  </div>
                )}
                <div>
                  <DiscordConnection
                    connected={formData.discord_connected || false}
                    onConnect={handleDiscordConnect}
                    onDisconnect={handleDiscordDisconnect}
                    userType={profile.user_type}
                  />
                </div>
              </div>

            </CardContent>
          </Card>

          {/* Preferences */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Preferences
              </CardTitle>
              <CardDescription>Manage your email preferences</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="newsletter"
                  checked={formData.is_newsletter || false}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_newsletter: checked as boolean }))}
                  className="mt-1"
                />
                <div className="grid gap-1.5 leading-none">
                  <Label
                    htmlFor="newsletter"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    Subscribe to the newsletter
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    By subscribing, you agree to receive our newsletter game updates and events notifications. You can unsubscribe at any time. See our <Link to="/privacy-policy" className="underline hover:text-primary">Privacy Policy</Link>.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving} size="lg">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </div>
    </Layout >
  );
}
